import React from 'react'
import { Switch, useRouteMatch } from 'react-router'

import { routes, useFlag } from '@sketch/modules-common'
import {
  PrivateWorkspaceRoute,
  WorkspaceDocumentsView,
  WorkspaceDocumentsViewRoutesProps,
  PersonalAuthRoute,
  PrivateRoute,
  Updates,
  UpdatesOld,
  UpdatesRoutesProps,
  WorkspaceProfileEditView,
  WorkspaceSidebarLayout,
  WorkspaceSidebarLayoutExtraProps,
} from 'cloud-frontend'

import { UserSettingsView } from '@sketch/sub-app-settings'

const DiscoverView = React.lazy(
  () => import('cloud-frontend/dist/esm/modules/workspace/views/DiscoverView')
)

const WorkspaceSidebarLayoutRoutesSwitch = (
  layoutProps: WorkspaceSidebarLayoutExtraProps
) => {
  // TODO: Remove profiles FF
  // https://github.com/sketch-hq/Cloud/issues/14920
  const isProfileFFEnabled = useFlag('profiles')
  // TODO: Remove updates-page-revamp FF
  // https://github.com/sketch-hq/Cloud/issues/17965
  const isUpdatesPageRevampFFEnabled = useFlag('updates-page-revamp')

  return (
    <Switch>
      <PrivateWorkspaceRoute
        path={routes.WORKSPACE_DISCOVER.template()}
        render={routeProps => <DiscoverView {...layoutProps} {...routeProps} />}
      />

      <PrivateWorkspaceRoute
        exact
        path={[
          routes.WORKSPACE_PROJECT.template(),
          routes.WORKSPACE_SHARES.template(),
          routes.WORKSPACE_LIBRARIES.template(),
          routes.WORKSPACE_TRASH.template(),
          routes.WORKSPACE_DRAFTS.template(),
          routes.WORKSPACE_SHARED_WITH_ME.template(),
          routes.WORKSPACE_TRASH_PROJECT.template(),
          routes.WORKSPACE_COLLECTION.template(),
          routes.WORKSPACE_TRASH_COLLECTION.template(),
          routes.WORKSPACE_ARCHIVE.template(),
        ]}
        render={routeProps => (
          <WorkspaceDocumentsView
            {...layoutProps}
            {...(routeProps as WorkspaceDocumentsViewRoutesProps)}
          />
        )}
      />

      {isProfileFFEnabled && (
        <PrivateWorkspaceRoute
          exact
          path={routes.WORKSPACE_PROFILE_EDIT.template()}
          render={routeProps => (
            <WorkspaceProfileEditView {...layoutProps} {...routeProps} />
          )}
        />
      )}

      <PrivateWorkspaceRoute
        exact
        path={routes.WORKSPACE_TEMPLATES.template()}
        render={routeProps => (
          <WorkspaceDocumentsView
            {...layoutProps}
            {...(routeProps as WorkspaceDocumentsViewRoutesProps)}
          />
        )}
      />

      {isUpdatesPageRevampFFEnabled ? (
        <>
          <PrivateRoute
            exact
            path={routes.UPDATES_UNREAD.template()}
            render={routeProps => (
              <Updates
                {...layoutProps}
                {...(routeProps as UpdatesRoutesProps)}
              />
            )}
          />
          <PrivateRoute
            exact
            path={routes.UPDATES_READ.template()}
            render={routeProps => (
              <Updates
                {...layoutProps}
                {...(routeProps as UpdatesRoutesProps)}
              />
            )}
          />
        </>
      ) : (
        <PrivateRoute
          exact
          path={routes.UPDATES.template()}
          render={routeProps => (
            <UpdatesOld
              {...layoutProps}
              {...(routeProps as UpdatesRoutesProps)}
            />
          )}
        />
      )}

      {/* User */}
      <PersonalAuthRoute
        exact
        path={[
          routes.PERSONAL_SETTINGS.template(),
          routes.PERSONAL_SETTINGS_UNSUBSCRIBE.template(),
        ]}
      >
        <PrivateRoute
          render={routeProps => (
            <UserSettingsView
              {...layoutProps}
              {...(routeProps as WorkspaceDocumentsViewRoutesProps)}
            />
          )}
        />
      </PersonalAuthRoute>

      {/*
        Access Tokens can be created with either SSO or Personal
        credentials, so although they are under `/settings/...` we don't
        want to enforce the use of Personal credentials as it could lead to
        incorrect credentials being used when creating tokens.
      */}
      <PrivateRoute
        exact
        path={[
          routes.PERSONAL_SETTINGS_ACCESS_TOKENS.template(),
          routes.PERSONAL_SETTINGS_ACCESS_TOKENS_CREATE.template(),
          routes.APPEARANCE_SETTINGS.create({}),
        ]}
        render={routeProps => (
          <UserSettingsView
            {...layoutProps}
            {...(routeProps as WorkspaceDocumentsViewRoutesProps)}
          />
        )}
      />
    </Switch>
  )
}

export const WorkspaceSidebarLayoutRoutes = () => {
  const isEmbeddedDiscover = useFlag('embedded-discover')
  const match = !!useRouteMatch(routes.WORKSPACE_DISCOVER.template())

  return (
    <WorkspaceSidebarLayout
      hideFooter={isEmbeddedDiscover && match}
      render={layoutProps => (
        <WorkspaceSidebarLayoutRoutesSwitch {...layoutProps} />
      )}
    />
  )
}
